import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment,
  List, ListItemAvatar, ListItemButton, ListItemText, MenuItem, Stack, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  ArrowDownward as DescIcon,
  ArrowUpward as AscIcon,
  Close as CloseIcon,
  PeopleOutline as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import unifiedApi, { buildApiUrl } from '../../utils/unifiedApi';
import { initialsFor } from '../../utils/chatFormat';

const compareText = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

/**
 * Sorts channel members in the UI. `orderBy` 'firstName' sorts first then last name; 'lastName' sorts last then
 * first name. Ties fall back to userTenantId so the order is stable.
 */
export const sortChannelMembers = (members, orderBy = 'firstName', direction = 'asc') => {
  const [primary, secondary] = orderBy === 'lastName' ? ['lastName', 'firstName'] : ['firstName', 'lastName'];
  const sign = direction === 'desc' ? -1 : 1;
  return [...(members || [])].sort((a, b) => sign * (
    compareText(a[primary], b[primary])
    || compareText(a[secondary], b[secondary])
    || (a.userTenantId - b.userTenantId)
  ));
};

// Lower-cased with accents removed, so "dube" finds "Dubé".
const fold = (text) => (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Members whose first or last name contains every search term (case- and accent-insensitive). */
export const filterChannelMembers = (members, query) => {
  const terms = fold(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return members || [];
  return (members || []).filter((m) => {
    const name = fold(`${m.firstName} ${m.lastName}`);
    return terms.every((term) => name.includes(term));
  });
};

const fullName = (member) => `${member.firstName || ''} ${member.lastName || ''}`.trim();

/**
 * Header button showing how many people are in a chat channel; opens a Slack-style member list with search and
 * sorting (all client-side). Members are fetched once per channel and refreshed each time the list is opened.
 */
const ChatChannelMembers = ({ channelId, channelName, currentUserTenantId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [orderBy, setOrderBy] = useState('firstName');
  const [direction, setDirection] = useState('asc');
  // Only the most recent request may update state; older (superseded or unmounted) responses are ignored.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;
    setLoading(true);
    setError(null);
    try {
      const result = await unifiedApi.getChatChannelMembers(channelId);
      if (!isCurrent()) return;
      if (Array.isArray(result)) setMembers(result);
      else setError('Channel members are unavailable.');
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Failed to load channel members.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    setMembers(null);
    setOpen(false);
    setQuery('');
    load();
    return () => { requestIdRef.current += 1; };
  }, [load]);

  const visible = useMemo(
    () => sortChannelMembers(filterChannelMembers(members, query), orderBy, direction),
    [members, query, orderBy, direction],
  );

  const handleOpen = () => {
    setOpen(true);
    load();
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
  };

  const count = members?.length;
  const countLabel = count == null ? 'Members' : `${count} ${count === 1 ? 'member' : 'members'}`;
  const nextDirectionLabel = direction === 'asc' ? 'Sort Z to A' : 'Sort A to Z';

  return (
    <>
      <Tooltip title="View members">
        <Button
          onClick={handleOpen}
          size="small"
          color="inherit"
          startIcon={<PeopleIcon />}
          aria-label={count == null ? 'View channel members' : `View ${countLabel}`}
          data-testid="chat-members-button"
          sx={{
            flexShrink: 0, minWidth: 0, px: 1, color: 'text.secondary', '& .MuiButton-startIcon': { mr: 0.5 },
          }}
        >
          {count == null ? (loading ? <CircularProgress size={14} color="inherit" /> : '–') : count}
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        aria-labelledby="chat-members-title"
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, height: { sm: 'min(720px, 85vh)' } } }}
      >
        <DialogTitle id="chat-members-title" sx={{ pr: 7, pb: 0.5 }}>
          <Typography component="span" variant="h6" fontWeight={700} sx={{ display: 'block', wordBreak: 'break-word' }}>
            {channelName || 'Members'}
          </Typography>
          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {countLabel}
          </Typography>
          <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Find people"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Find people' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <TextField
              select
              size="small"
              value={orderBy}
              onChange={(event) => setOrderBy(event.target.value)}
              SelectProps={{ SelectDisplayProps: { 'aria-label': 'Sort by' } }}
              sx={{ minWidth: 140, flex: { xs: 1, sm: 'none' } }}
            >
              <MenuItem value="firstName">First name</MenuItem>
              <MenuItem value="lastName">Last name</MenuItem>
            </TextField>
            <Tooltip title={nextDirectionLabel}>
              <IconButton
                onClick={() => setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                aria-label={nextDirectionLabel}
                size="small"
              >
                {direction === 'asc' ? <AscIcon fontSize="small" /> : <DescIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <DialogContent sx={{ px: 1, py: 1 }}>
          {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
          {!error && members == null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
          )}
          {!error && members != null && (
            <List aria-label="Channel members" disablePadding>
              {visible.map((member) => {
                const name = fullName(member);
                const isYou = currentUserTenantId != null && member.userTenantId === currentUserTenantId;
                return (
                  <ListItemButton
                    key={member.userTenantId}
                    component={RouterLink}
                    to={`/player/${member.userTenantId}`}
                    data-testid="chat-member-row"
                    sx={{ borderRadius: 1.5, py: 1, px: 2 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 52 }}>
                      <Avatar
                        variant="rounded"
                        src={member.photoUrl ? buildApiUrl(member.photoUrl) : undefined}
                        alt={name}
                        sx={{ width: 36, height: 36, borderRadius: 1.5, fontSize: 14, fontWeight: 600 }}
                      >
                        {initialsFor(name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={isYou ? `${name} (you)` : name}
                      primaryTypographyProps={{ fontWeight: 700, sx: { wordBreak: 'break-word' }, 'data-testid': 'chat-member-name' }}
                    />
                  </ListItemButton>
                );
              })}
              {visible.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
                  {query.trim() ? `No members match “${query.trim()}”` : 'No members'}
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatChannelMembers;
